"use client"

import { FormikProvider, type FormikErrors, useFormik } from "formik"

import { FormSelect } from "@/components/form-inputs"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  useMarkDeliveryCrmHandoffVerifiedMutation,
  useRetryDeliveryCrmHandoffMutation,
} from "@/hooks/mutations/delivery-shipments"
import { DELIVERY_CRM_UNKNOWN_ERROR_CODE } from "@/lib/deliveries/crm-handoff-constants"
import type { DeliveryShipmentListItem } from "@/server-actions/delivery-shipments"

type RecoveryVerification = "not_checked" | "note_exists" | "note_absent"

type RecoveryFormValues = {
  verification: RecoveryVerification
}

const initialValues: RecoveryFormValues = { verification: "not_checked" }
const verificationOptions = [
  { value: "note_exists", label: "The worker note exists and the CRM status is correct" },
  { value: "note_absent", label: "The worker note is absent" },
] as const

export function RecoverDeliveryCrmHandoffDialog({
  shipment,
  onOpenChange,
}: {
  shipment: DeliveryShipmentListItem | null
  onOpenChange: (open: boolean) => void
}) {
  const retryMutation = useRetryDeliveryCrmHandoffMutation()
  const verifiedMutation = useMarkDeliveryCrmHandoffVerifiedMutation()
  const requiresVerification = shipment?.crmLastErrorCode
    === DELIVERY_CRM_UNKNOWN_ERROR_CODE
  const formik = useFormik<RecoveryFormValues>({
    initialValues,
    enableReinitialize: true,
    validate: values => validateRecovery(values, requiresVerification),
    onSubmit: async (values, helpers) => {
      if (!shipment) return
      try {
        switch (values.verification) {
          case "note_exists":
            await verifiedMutation.mutateAsync({
              shipmentId: shipment.id,
              confirmedNoteExists: true,
            })
            break
          case "note_absent":
            await retryMutation.mutateAsync({
              shipmentId: shipment.id,
              confirmedNoteAbsent: true,
            })
            break
          case "not_checked":
            await retryMutation.mutateAsync({ shipmentId: shipment.id })
            break
          default: {
            const exhaustiveVerification: never = values.verification
            throw new Error(
              `Unsupported CRM recovery choice: ${exhaustiveVerification}`,
            )
          }
        }
        helpers.resetForm()
        onOpenChange(false)
      } catch {
        // withQueryHandling has already surfaced the safe server error.
      }
    },
  })

  return (
    <Dialog open={shipment !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Recover CRM worker handoff</DialogTitle>
          <DialogDescription>
            This only repairs the CRM notification. It never creates another
            Nova Post waybill.
          </DialogDescription>
        </DialogHeader>
        {requiresVerification ? (
          <Alert>
            <AlertTitle>Check the CRM before retrying</AlertTitle>
            <AlertDescription>
              <span>The previous CRM response was inconclusive. Search the opportunity
              for marker</span> <span className="font-mono text-xs">
                {shipment?.crmFailureNoteMarker ?? (shipment ? `[versafy-delivery-shipment:${shipment.id}]` : "")}
              </span><span>. Recording an existing note avoids a duplicate; retry only
              after confirming that it is absent. Before marking the handoff complete,
              verify that the configured CRM status was applied too. If the note exists
              but the status is wrong, correct the status in CRM first.</span>
            </AlertDescription>
          </Alert>
        ) : (
          <Alert>
            <AlertTitle>The waybill will not be recreated</AlertTitle>
            <AlertDescription>
              Versafy will retry the CRM handoff: the structured worker note and
              configured status, without creating another waybill.
            </AlertDescription>
          </Alert>
        )}
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            {requiresVerification ? (
              <FormSelect
                name="verification"
                label="CRM verification result"
                options={verificationOptions}
                placeholder="Select what you found"
              />
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={formik.isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={formik.isSubmitting || !shipment}>
                {formik.isSubmitting ? "Processing..." : "Continue"}
              </Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}

function validateRecovery(
  values: RecoveryFormValues,
  requiresVerification: boolean,
): FormikErrors<RecoveryFormValues> {
  if (requiresVerification && values.verification === "not_checked") {
    return { verification: "Select what you found in the CRM." }
  }
  return {}
}
